import React, { useState, useEffect, useRef } from "react";
import { UploadOutlined, CloseOutlined } from "@ant-design/icons";
import { useEdit } from "../../context/EditContext";
import { apiFetch } from "../../../core/utils/api";
import InfoIcon from "../InfoIcon";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import { NetworkErrorState } from "../../../design-system/components/layouts";

// File type icons mapping - using simple SVG icons
const FILE_TYPE_ICONS = {
  pdf: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  doc: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  docx: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  xls: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  xlsx: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  ppt: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <circle cx="8" cy="14" r="2" />
      <path d="M10 12h4" />
    </svg>
  ),
  pptx: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <circle cx="8" cy="14" r="2" />
      <path d="M10 12h4" />
    </svg>
  ),
  txt: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  rtf: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  csv: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
};

const ALLOWED_FILE_TYPES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.rtf', '.csv'];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

/**
 * Extract the S3 key (path) from a URL — plain or presigned.
 * Strips the hostname and query-string so we get a stable identifier
 * like "private/123/files/resume.pdf" regardless of signature.
 */
const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  try {
    const withoutQuery = url.split('?')[0];
    const match = withoutQuery.match(/\.s3\.amazonaws\.com\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

/** Strip the query-string from a URL before calling split('/').pop(). */
const basenameFromUrl = (url) => {
  if (!url) return '';
  return url.split('?')[0].split('/').pop();
};

/**
 * Returns a "tail" segment that tries to preserve the file extension.
 * Example: "some-long-name.pdf" -> "...name.pdf" (tail includes ".pdf")
 */
const getFilenameTail = (filename, desiredTailLength = 14) => {
  if (!filename || typeof filename !== "string") return "";
  if (filename.length <= desiredTailLength) return filename;

  const lastDot = filename.lastIndexOf(".");
  const hasReasonableExt = lastDot > 0 && filename.length - lastDot <= 8;

  if (!hasReasonableExt) {
    return filename.slice(-desiredTailLength);
  }

  const ext = filename.slice(lastDot);
  const base = filename.slice(0, lastDot);
  const baseTailLen = Math.max(0, desiredTailLength - ext.length);
  const baseTail = baseTailLen > 0 ? base.slice(-baseTailLen) : "";

  return `${baseTail}${ext}`;
};

/**
 * Displays text as either:
 * - narrow: standard end-ellipsis (beginning only)
 * - wide: middle-ellipsis by keeping the tail visible (beginning...tail)
 */
const ResponsiveMiddleTruncate = ({
  text,
  className = "",
  desiredTailLength = 14,
  narrowWidthPx = 260,
}) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => setWidth(el.getBoundingClientRect().width);
    update();

    const observer = new ResizeObserver(() => update());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const safeText = typeof text === "string" ? text : "";
  const isNarrow = width !== null && width < narrowWidthPx;

  // Narrow: show only the beginning (regular ellipsis at end)
  if (isNarrow || !safeText) {
    return (
      <span
        ref={ref}
        className={`block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap ${className}`}
        title={safeText}
      >
        {safeText}
      </span>
    );
  }

  const tail = getFilenameTail(safeText, desiredTailLength);
  const head = tail && tail.length < safeText.length ? safeText.slice(0, safeText.length - tail.length) : safeText;

  // Wide: keep the tail visible; head gets ellipsis when constrained
  return (
    <span ref={ref} className={`flex min-w-0 ${className}`} title={safeText}>
      <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{head}</span>
      {tail && tail.length < safeText.length ? (
        <span className="flex-shrink-0 whitespace-nowrap">{tail}</span>
      ) : null}
    </span>
  );
};

export const FileField = ({ label, help, value, onChange, onOriginalFilenameChange, onDisplayNameChange, onMultiFieldUpdate, infoText }) => {
  const { websiteId } = useEdit();
  const [isLoading, setIsLoading] = useState(false);
  const [s3Files, setS3Files] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const fileInputRef = useRef(null);

  // Load user files when component mounts
  useEffect(() => {
    loadUserFiles();
  }, []);

  // Set initial selected file based on current value.
  // Compare by S3 key (stable) rather than signed URL (changes every request).
  useEffect(() => {
    if (value && s3Files.length > 0) {
      const currentKey = extractS3KeyFromUrl(value);
      const currentFile = currentKey
        ? s3Files.find(file => file.key === currentKey)
        : s3Files.find(file => file.url === value);
      if (currentFile) {
        setSelectedFileId(currentFile.key || currentFile.url);
      }
    }
  }, [value, s3Files]);

  const loadUserFiles = async () => {
    setIsLoading(true);
    setUploadError("");

    try {
      // Use session-based endpoint
      const response = await apiFetch(`/api/s3-files/`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setS3Files(data.files || []);
      } else if (response.status === 401) {
        setUploadError("Please log in to access files");
      } else {
        setUploadError("Failed to load files");
      }
    } catch (error) {
      console.error("Error loading files:", error);
      setUploadError("Network error loading files");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!websiteId) {
      setUploadError("Website ID not available");
      return;
    }

    setIsLoading(true);
    setUploadError("");

    const formData = new FormData();
    formData.append('file', file);
    formData.append('website_id', websiteId);

    try {
      // Use session-based upload endpoint
      const response = await apiFetch(`/api/upload-file/`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Reload user files to include the new one
        await loadUserFiles();
        
        // Automatically select the newly uploaded file
        if (data.file) {
          const fileUrl = data.file.url;
          setSelectedFileId(data.file.key || fileUrl);
          
          // If multi-field update is available, use it to send all fields at once
          if (onMultiFieldUpdate) {
            const updates = {
              file_url: fileUrl
            };
            
            if (data.file.original_filename) {
              updates.original_filename = data.file.original_filename;
              updates.file_name = data.file.original_filename; // Set display name to original filename by default
            }
            
            onMultiFieldUpdate(updates);
          } else {
            // Fallback to individual updates
            onChange(fileUrl);
            
            if (onOriginalFilenameChange && data.file.original_filename) {
              onOriginalFilenameChange(data.file.original_filename);
            }
            
            if (onDisplayNameChange && data.file.original_filename) {
              onDisplayNameChange(data.file.original_filename);
            }
          }
        }
      } else if (response.status === 401) {
        setUploadError("Please log in to upload files");
      } else {
        const errorData = await response.json();
        setUploadError(errorData.error || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadError("Network error during upload");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(fileExtension)) {
        setUploadError(`File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}`);
        return;
      }
      
      // Validate file size (25MB limit)
      if (file.size > MAX_FILE_SIZE) {
        setUploadError("File size must be less than 25MB");
        return;
      }

      handleFileUpload(file);
    }
  };

  const handleFileSelectionChange = (file) => {
    const fileId = file.key || file.url;
    setSelectedFileId(fileId);
    
    // If multi-field update is available, use it to send all fields at once
    if (onMultiFieldUpdate) {
      const updates = {
        file_url: file.url
      };
      
      if (file.original_filename) {
        updates.original_filename = file.original_filename;
        updates.file_name = file.original_filename; // Set display name to original filename by default
      }
      
      onMultiFieldUpdate(updates);
    } else {
      // Fallback to individual updates
      onChange(file.url);
      
      if (onOriginalFilenameChange && file.original_filename) {
        onOriginalFilenameChange(file.original_filename);
      }
      
      if (onDisplayNameChange && file.original_filename) {
        onDisplayNameChange(file.original_filename);
      }
    }
  };

  const handleUnselectFile = () => {
    setSelectedFileId(null);
    onChange("");
  };

  const handleDeleteClick = (file, event) => {
    event.stopPropagation();
    setFileToDelete(file);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    const fileKey = fileToDelete.key || fileToDelete.url;
    setDeletingFiles(prev => new Set([...prev, fileKey]));
    setShowDeleteConfirm(false);


    try {
      const response = await apiFetch('/api/delete-s3-file/', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ s3_key: fileToDelete.key }),
        credentials: 'include'
      });

      if (response.ok) {
        // Remove from local state
        setS3Files(prevFiles => prevFiles.filter(f => (f.key || f.url) !== fileKey));
        
        // If this was the selected file, clear selection
        if (selectedFileId === fileKey) {
          setSelectedFileId(null);
          onChange("");
        }
      } else {
        const errorData = await response.json();
        setUploadError(errorData.error || "Failed to delete file");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      setUploadError("Network error during deletion");
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileKey);
        return newSet;
      });
      setFileToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setFileToDelete(null);
  };

  const getFileIcon = (filename) => {
    const extension = filename ? filename.split('.').pop()?.toLowerCase() : '';
    return FILE_TYPE_ICONS[extension] || FILE_TYPE_ICONS.txt;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const sizes = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < sizes.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${sizes[unitIndex]}`;
  };

  const isNetworkError =
    typeof uploadError === "string" && uploadError.toLowerCase().includes("network error");

  return (
    <div className="space-y-4">
      {/* Label and Help */}
      <div>
        <label className={`block mb-1 ${typography.label} ${textColors.secondary}`}>
          {label || "File"}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
        {help && (
          <p className={`${typography.caption} ${textColors.tertiary}`}>{help}</p>
        )}
      </div>

      {/* Error Display */}
      {uploadError && isNetworkError ? (
        <NetworkErrorState
          title="Network error :("
          description="We couldn't load files right now. Please check your connection and try again."
          onRetry={loadUserFiles}
          className="py-6"
        />
      ) : uploadError ? (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p className={`text-red-600 dark:text-red-400 ${typography.bodySmall}`}>{uploadError}</p>
        </div>
      ) : null}

      {/* Current File Display */}
      {value && (() => {
        // Match the selected file by key (stable across signed URL rotations)
        const currentKey = extractS3KeyFromUrl(value);
        const matchedFile = currentKey
          ? s3Files.find(f => f.key === currentKey)
          : s3Files.find(f => f.url === value);
        const displayFilename = matchedFile?.original_filename || basenameFromUrl(value);
        return (
        <div className="relative inline-block group">
          <div className={`flex items-center space-x-3 p-3 border ${borders.default} rounded-lg ${surfaces.secondary}`}>
            <div className={`text-2xl flex-shrink-0 ${textColors.primary}`}>
              {getFileIcon(basenameFromUrl(value))}
            </div>
            <div className="flex-1 min-w-0">
              <ResponsiveMiddleTruncate
                text={displayFilename}
                className={`font-medium ${textColors.primary}`}
                desiredTailLength={18}
                narrowWidthPx={240}
              />
              <div className={`${typography.caption} ${textColors.muted}`}>
                {formatFileSize(matchedFile?.size)}
              </div>
            </div>
            <button
              onClick={handleUnselectFile}
              className={`${textColors.tertiary} hover:${textColors.secondary} transition-colors p-1 rounded-full ${surfaces.hover}`}
              title="Unselect file"
            >
              <CloseOutlined className="h-4 w-4" />
            </button>
          </div>
        </div>
        );
      })()}

      {/* Upload Button */}
      <div className="flex flex-col items-start gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          className={`inline-flex items-center px-4 py-2 border ${borders.default} rounded-md shadow-sm ${typography.button} ${textColors.primary} ${surfaces.primary} ${surfaces.hover} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <UploadOutlined className="mr-2" />
          {isLoading ? "Uploading..." : "Upload File"}
        </button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_FILE_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={isLoading}
        />
        
        <div className={`${typography.caption} ${textColors.muted}`}>
          Max 25MB • {ALLOWED_FILE_TYPES.join(', ')}
        </div>
      </div>

      {/* File Selection Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 dark:border-blue-400"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
          {s3Files.filter(file => {
            if (!value) return true;
            const currentKey = extractS3KeyFromUrl(value);
            return currentKey ? file.key !== currentKey : file.url !== value;
          }).map((file) => {
            const isDeleting = deletingFiles.has(file.key);
            
            return (
              <div
                key={file.key || file.url}
                className={`relative border ${borders.default} rounded-lg p-3 cursor-pointer transition-all duration-200 ${surfaces.hover} ${isDeleting ? "opacity-50" : ""}`}
                onClick={() => !isDeleting && handleFileSelectionChange(file)}
              >
                <div className="flex items-center space-x-3">
                  {/* File Icon */}
                  <div className={`text-2xl flex-shrink-0 ${textColors.primary}`}>
                    {getFileIcon(file.filename)}
                  </div>
                  
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <ResponsiveMiddleTruncate
                      text={file.original_filename || file.filename}
                      className={`font-medium ${textColors.primary}`}
                      desiredTailLength={18}
                      narrowWidthPx={240}
                    />
                    <div className={`flex items-center space-x-2 ${typography.caption} ${textColors.muted}`}>
                      <span>{formatFileSize(file.size)}</span>
                      {file.last_modified && (
                        <>
                          <span>•</span>
                          <span>{new Date(file.last_modified).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteClick(file, e)}
                    disabled={isDeleting}
                    className="text-red-400 hover:text-red-600 dark:hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Delete file"
                  >
                    {isDeleting ? (
                      <div className="animate-spin h-4 w-4 border-2 border-red-400 border-t-transparent rounded-full"></div>
                    ) : (
                      <CloseOutlined className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
          
          {s3Files.filter(file => {
            if (!value) return true;
            const currentKey = extractS3KeyFromUrl(value);
            return currentKey ? file.key !== currentKey : file.url !== value;
          }).length === 0 && (
            <div className={`text-center py-8 ${textColors.secondary}`}>
              <div className={`w-10 h-10 mx-auto mb-2 ${textColors.tertiary}`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-full h-full"
                >
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                </svg>
              </div>
              <div className={typography.bodySmall}>Upload your files above</div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${surfaces.primary} rounded-lg p-6 max-w-sm w-full mx-4`}>
            <h3 className={`${typography.h5} ${textColors.primary} mb-2`}>Delete File</h3>
            <p className={`${typography.bodySmall} ${textColors.secondary} mb-4`}>
              Are you sure you want to delete "{fileToDelete?.original_filename || fileToDelete?.filename}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
              <button
                onClick={cancelDelete}
                className={`flex-1 ${surfaces.tertiary} ${textColors.primary} px-4 py-2 rounded-md ${typography.button} ${surfaces.hover} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
